import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  UploadCloud, Cpu, FileText, Waves, ShieldCheck, LockKeyhole,
  FileAudio, LineChart, Download, Check, Users, ArrowRight, Menu, X,
  TrendingUp, Headset, UserCog, GraduationCap, ClipboardCheck, Store,
} from "lucide-react";
import { motion } from "framer-motion";
import { jsPDF } from "jspdf";
import { GlexisInAction } from "@/components/GlexisInAction";

const fade = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.5 },
};

const SAMPLE_DISCLAIMER =
  "Illustrative sample with fictional content. Glexis surfaces communication indicators to support human judgment and coaching — it does not prove deception and does not make absolute or final judgments about any person. Actual reports reflect your own uploaded conversation.";

const SAMPLE = {
  id: "A2F19",
  overall: { score: "92%", label: "High Confidence" },
  metrics: [
    ["Vagueness", "Low"],
    ["Emotional State", "Stable"],
    ["Repetition Index", "Minimal"],
    ["Communication Signal", "High"],
  ],
  summary:
    "The speaker maintained a consistently high confidence profile with a stable emotional tone throughout the conversation. Overall vagueness and repetition remained minimal, yielding a strong communication-confidence signal. A brief dip in specificity appears when timelines are discussed.",
  overview: [
    ["Participants", "Speaker A (Renovation Advisor) · Speaker B (Homeowner)"],
    ["Duration", "18 min 42 sec"],
    ["Topic", "Kitchen renovation quote, materials, and timeline"],
    ["Setting", "Recorded consultation call (fictional)"],
  ],
  transcript: [
    ["01:12", "Speaker A", "So for the cabinets we'd be looking at roughly three to four weeks once materials arrive."],
    ["01:20", "Speaker B", "And the materials themselves — are those in stock right now?"],
    ["01:24", "Speaker A", "Most of them, yes. The countertop is the one item that can vary a little."],
    ["07:58", "Speaker A", "The estimate lands around twelve-five, give or take, depending on the finish you choose."],
  ],
  indicators: [
    ["Clarity", "High"],
    ["Consistency", "Moderate"],
    ["Specificity", "Moderate"],
    ["Hesitation markers", "Low"],
    ["Active listening cues", "High"],
    ["Follow-through on questions", "Moderate"],
  ],
  patterns: [
    "Warm, steady tone across most of the conversation, with engaged back-and-forth.",
    "A short rise in filler words and softer phrasing when exact dates were requested.",
    "Confidence remained high when discussing materials and craftsmanship.",
  ],
  contextAreas: [
    "The timeline was described first as \"three to four weeks\" and later as \"about a month\" — likely consistent, but worth confirming a firm date.",
    "The cost was given as a range (\"around twelve-five\") and elsewhere as a fixed figure; clarifying what is included would remove ambiguity.",
    "These are areas where more context would help — not indications of dishonesty.",
  ],
  strengths: [
    "Clear, jargon-light explanations that kept the homeowner engaged.",
    "Responsive to questions and acknowledged concerns before answering.",
    "Steady, reassuring tone that built rapport.",
  ],
  takeaways: [
    "Confirm a single, firm start-and-finish date in writing.",
    "Restate the estimate with an itemized list of what is and isn't included.",
    "Summarize agreed next steps at the end of the call to lock in alignment.",
  ],
  questions: [
    "Can you put the exact start and completion dates in the written quote?",
    "Which specific items could change the countertop price, and by how much?",
    "What happens to the timeline if the countertop material is delayed?",
    "Does the estimate include removal, disposal, and finishing work?",
  ],
};

function downloadSamplePdf() {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const margin = 48;
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const width = pageW - margin * 2;
  let y = margin;

  const write = (text, { size = 11, bold = false, color = [51, 65, 85], gap = 8 } = {}) => {
    doc.setFont("helvetica", bold ? "bold" : "normal");
    doc.setFontSize(size);
    doc.setTextColor(color[0], color[1], color[2]);
    const lines = doc.splitTextToSize(text, width);
    lines.forEach((ln) => {
      if (y > pageH - margin) { doc.addPage(); y = margin; }
      doc.text(ln, margin, y);
      y += size + 3;
    });
    y += gap;
  };
  const bullets = (title, arr) => {
    write(title, { size: 13, bold: true, color: [8, 17, 36] });
    arr.forEach((x) => write("•  " + x));
  };

  write("GLEXIS — Communication Analysis", { size: 18, bold: true, color: [8, 17, 36], gap: 4 });
  write("ILLUSTRATIVE SAMPLE — fictional content, not a real analysis.", { size: 10, bold: true, color: [180, 120, 10] });
  write(`Report ID: analysis-report-#${SAMPLE.id}`, { size: 10, color: [100, 116, 139] });

  write("Summary", { size: 13, bold: true, color: [8, 17, 36] });
  write(`Overall Confidence: ${SAMPLE.overall.score} (${SAMPLE.overall.label})`);
  write(SAMPLE.metrics.map(([k, v]) => `${k}: ${v}`).join("    |    "));
  write(SAMPLE.summary);

  write("Conversation Overview", { size: 13, bold: true, color: [8, 17, 36] });
  SAMPLE.overview.forEach(([k, v]) => write(`${k}: ${v}`, { gap: 3 }));
  y += 5;

  write("Transcript Excerpts", { size: 13, bold: true, color: [8, 17, 36] });
  SAMPLE.transcript.forEach(([t, s, l]) => write(`[${t}] ${s}: ${l}`, { gap: 3 }));
  y += 5;

  write("Communication Indicators", { size: 13, bold: true, color: [8, 17, 36] });
  write(SAMPLE.indicators.map(([k, v]) => `${k}: ${v}`).join("    |    "));

  bullets("Emotional and Behavioral Patterns", SAMPLE.patterns);
  bullets("Inconsistencies or Areas Requiring Context", SAMPLE.contextAreas);
  bullets("Communication Strengths", SAMPLE.strengths);
  bullets("Practical Takeaways", SAMPLE.takeaways);
  bullets("Suggested Questions for a Future Conversation", SAMPLE.questions);

  write(SAMPLE_DISCLAIMER, { size: 9, color: [100, 116, 139] });

  doc.save("glexis-sample-report.pdf");
}

export default function Landing() {
  const { user } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const primaryTo = user ? "/dashboard" : "/signup";

  return (
    <div className="min-h-screen bg-[#050914] text-slate-100 overflow-x-hidden">
      {/* Nav */}
      <header className="sticky top-0 z-50 border-b border-slate-800/80 bg-[#050914]/80 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-8">
          <Logo variant="icon" size="lg" className="rounded-xl" />
          <nav className="hidden md:flex items-center gap-8 text-sm text-slate-400">
            <a href="#how" className="hover:text-white transition-colors">How it works</a>
            <a href="#capabilities" className="hover:text-white transition-colors">Capabilities</a>
            <a href="#sample" className="hover:text-white transition-colors">Sample report</a>
            <a href="#pricing" className="hover:text-white transition-colors">Pricing</a>
            <a href="#faq" className="hover:text-white transition-colors">FAQ</a>
          </nav>
          <div className="hidden md:flex items-center gap-3">
            {user ? (
              <Button asChild className="bg-cyan-400 text-black font-semibold hover:bg-cyan-300">
                <Link to="/dashboard" data-testid="nav-dashboard-btn">Open app</Link>
              </Button>
            ) : (
              <>
                <Button asChild variant="ghost" className="text-slate-300 hover:text-white hover:bg-slate-800">
                  <Link to="/login" data-testid="nav-login-btn">Sign in</Link>
                </Button>
                <Button asChild className="bg-cyan-400 text-black font-semibold hover:bg-cyan-300 gx-glow">
                  <Link to="/signup" data-testid="nav-signup-btn">Get started</Link>
                </Button>
              </>
            )}
          </div>
          <button className="md:hidden text-slate-300" onClick={() => setMenuOpen(!menuOpen)} data-testid="mobile-menu-toggle">
            {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
        {menuOpen && (
          <div className="md:hidden border-t border-slate-800 px-4 py-4 space-y-3 bg-[#050914]">
            {["how", "capabilities", "sample", "pricing", "faq"].map((id) => (
              <a key={id} href={`#${id}`} onClick={() => setMenuOpen(false)} className="block text-slate-300 capitalize">{id}</a>
            ))}
            <div className="flex gap-3 pt-2">
              <Button asChild variant="outline" className="flex-1 border-slate-700 text-white hover:bg-slate-800">
                <Link to="/login">Sign in</Link>
              </Button>
              <Button asChild className="flex-1 bg-cyan-400 text-black font-semibold">
                <Link to={primaryTo}>Get started</Link>
              </Button>
            </div>
          </div>
        )}
      </header>

      {/* Hero */}
      <section className="relative">
        <div className="absolute inset-0 gx-grid-bg opacity-40" />
        <div className="absolute inset-0 gx-radial" />
        <div className="relative mx-auto max-w-6xl px-4 sm:px-8 pt-20 pb-28 text-center">
          <motion.img
            {...fade}
            src="/glexis-logo.png"
            alt="Glexis — Clarity, Confidence, Control"
            className="mx-auto h-32 sm:h-44 lg:h-52 w-auto object-contain select-none"
            draggable={false}
          />
          <motion.div {...fade} transition={{ duration: 0.5, delay: 0.05 }} className="mt-12 inline-flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-400/10 px-4 py-1.5 text-xs font-medium text-cyan-300">
            <Waves className="h-3.5 w-3.5" /> AI-Powered Communication Intelligence
          </motion.div>
          <motion.h1 {...fade} transition={{ duration: 0.5, delay: 0.05 }} className="mx-auto mt-8 max-w-4xl font-display text-4xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight text-white leading-[1.05]">
            Understand every{" "}
            <span className="text-cyan-400 gx-text-glow">conversation</span>
          </motion.h1>
          <motion.p {...fade} transition={{ duration: 0.5, delay: 0.1 }} className="mx-auto mt-6 max-w-2xl text-lg text-slate-400">
            Glexis uses AI to analyze communication patterns from your uploaded audio — delivering
            clarity, confidence, and deep communication insights in moments.
          </motion.p>
          <motion.div {...fade} transition={{ duration: 0.5, delay: 0.15 }} className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button asChild size="lg" className="h-12 px-8 bg-cyan-400 text-black font-semibold hover:bg-cyan-300 gx-glow">
              <Link to={primaryTo} data-testid="hero-cta">Open web app <ArrowRight className="h-4 w-4 ml-2" /></Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="h-12 px-8 border-slate-700 bg-slate-900/40 text-white hover:bg-slate-800 hover:text-white">
              <a href="#sample" data-testid="hero-sample">View sample report</a>
            </Button>
          </motion.div>
          <motion.div {...fade} transition={{ duration: 0.5, delay: 0.2 }} className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-x-6 gap-y-2 text-sm text-slate-500">
            <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-cyan-400" /> Secure encrypted connections</span>
            <span className="hidden sm:block text-slate-700">•</span>
            <span>1 complimentary full analysis — use within 24 hours</span>
          </motion.div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="border-t border-slate-800/60 py-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-8">
          <SectionHeader eyebrow="How It Works" title="Three steps to deeper clarity" desc="From upload to insight — a refined, secure pipeline engineered for precision." />
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {[
              { n: "01", icon: UploadCloud, t: "Upload Your Audio", d: "Securely upload an audio file of a conversation over an encrypted HTTPS connection." },
              { n: "02", icon: Cpu, t: "AI Analyzes Patterns", d: "Our engine examines communication patterns, repetition, vagueness, and behavioral signals." },
              { n: "03", icon: FileText, t: "Receive Your Report", d: "Get a detailed report with transcript, insights, confidence scoring, and communication signals." },
            ].map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div key={s.n} {...fade} transition={{ duration: 0.5, delay: i * 0.08 }}
                  className="rounded-xl border border-slate-800 bg-[#0A1124] p-8 transition-colors hover:border-slate-700">
                  <div className="flex items-center justify-between">
                    <div className="flex h-11 w-11 items-center justify-center rounded-lg border border-cyan-400/40 bg-cyan-400/10">
                      <Icon className="h-5 w-5 text-cyan-400" />
                    </div>
                    <span className="font-mono text-3xl font-bold text-slate-800">{s.n}</span>
                  </div>
                  <h3 className="mt-6 font-display text-xl font-semibold text-white">{s.t}</h3>
                  <p className="mt-2 text-slate-400">{s.d}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Capabilities bento */}
      <section id="capabilities" className="border-t border-slate-800/60 py-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-8">
          <SectionHeader eyebrow="Capabilities" title="Intelligence in every signal" desc="A complete suite of analytical instruments, built for those who need to know." />
          <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { icon: FileAudio, t: "Upload Audio Files", d: "Analyze any conversation by uploading an audio file. Common formats supported." },
              { icon: Cpu, t: "AI Communication Analysis", d: "Advanced models examine tone, consistency, and behavioral signals across the exchange." },
              { icon: FileText, t: "Transcript Generation", d: "Every analysis includes a complete, accurate transcript of your uploaded audio." },
              { icon: Waves, t: "Communication Insights", d: "Understand patterns, hesitation, and shifts in narrative with clear, actionable insights." },
              { icon: LineChart, t: "Confidence Indicators", d: "AI-generated confidence scores highlight patterns and behavioral signals across the analysis." },
              { icon: Download, t: "Download Reports", d: "Subscribers can export their full analysis as a polished report to save and share." },
            ].map((c, i) => {
              const Icon = c.icon;
              return (
                <motion.div key={c.t} {...fade} transition={{ duration: 0.5, delay: (i % 3) * 0.06 }}
                  className="group rounded-xl border border-slate-800 bg-[#0A1124] p-6 transition-all hover:-translate-y-1 hover:border-cyan-400/40">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-slate-700 bg-slate-900 transition-colors group-hover:border-cyan-400/40">
                    <Icon className="h-5 w-5 text-cyan-400" />
                  </div>
                  <h3 className="mt-5 font-display text-lg font-semibold text-white">{c.t}</h3>
                  <p className="mt-2 text-sm text-slate-400">{c.d}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Glexis for Business */}
      <section id="business" className="border-t border-slate-800/60 py-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-8">
          <SectionHeader
            eyebrow="Glexis for Business"
            title="Turn Everyday Conversations Into Business Insight"
            desc="Glexis helps businesses transform recorded conversations into meaningful communication insights. Teams can identify patterns in clarity, confidence, consistency, emotional tone, and communication style to support stronger coaching, better customer experiences, and more informed decisions."
          />
          <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { icon: TrendingUp, t: "Sales Teams", d: "Review sales conversations to identify communication patterns, strengthen presentations, improve rapport, and help teams understand which approaches create better customer engagement." },
              { icon: Headset, t: "Customer Service", d: "Analyze support conversations to improve clarity, empathy, active listening, and response quality while creating useful coaching opportunities for customer-facing teams." },
              { icon: UserCog, t: "Leadership and Management", d: "Help managers improve one-on-one conversations, team meetings, presentations, and feedback by showing communication strengths and areas for development." },
              { icon: GraduationCap, t: "Training and Development", d: "Compare conversations before and after training to help businesses evaluate progress in communication confidence, clarity, consistency, and effectiveness." },
              { icon: ClipboardCheck, t: "Recruiting and Interview Training", d: "Support recruiter and interviewer development by reviewing how questions are asked, how clearly candidates are engaged, and where the interview experience can improve. Glexis supports interviewer development only—it does not make hiring decisions or judge whether a candidate is truthful." },
              { icon: Store, t: "Small Businesses", d: "Give small-business owners an affordable way to coach sales conversations, customer interactions, employee communication, and service quality without needing a costly corporate training program." },
            ].map((c, i) => {
              const Icon = c.icon;
              return (
                <motion.div key={c.t} {...fade} transition={{ duration: 0.5, delay: (i % 3) * 0.06 }}
                  className="group rounded-xl border border-slate-800 bg-[#0A1124] p-6 transition-all hover:-translate-y-1 hover:border-cyan-400/40">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-slate-700 bg-slate-900 transition-colors group-hover:border-cyan-400/40">
                    <Icon className="h-5 w-5 text-cyan-400" aria-hidden="true" />
                  </div>
                  <h3 className="mt-5 font-display text-lg font-semibold text-white">{c.t}</h3>
                  <p className="mt-2 text-sm text-slate-400">{c.d}</p>
                </motion.div>
              );
            })}
          </div>

          <motion.div {...fade} className="mt-10 mx-auto max-w-3xl rounded-2xl border border-cyan-400/30 bg-cyan-400/[0.05] p-6 sm:p-8">
            <h3 className="font-display text-lg font-semibold text-white">Built to support human judgment—not replace it.</h3>
            <p className="mt-3 text-sm leading-relaxed text-slate-300">
              Glexis provides communication indicators and analytical insights that businesses can use
              for coaching, education, and decision support. Results should not be treated as absolute
              conclusions or used as the sole basis for employment, legal, financial, healthcare, or
              disciplinary decisions.
            </p>
          </motion.div>

          <div className="mt-10 text-center">
            <Button asChild size="lg" className="h-12 px-8 bg-cyan-400 text-black font-semibold hover:bg-cyan-300 gx-glow">
              <a href="#pricing" data-testid="business-cta">Explore Glexis for Business <ArrowRight className="h-4 w-4 ml-2" /></a>
            </Button>
          </div>
        </div>
      </section>

      {/* See Glexis in Action — interactive demo */}
      <GlexisInAction primaryTo={primaryTo} />

      {/* Sample report */}
      <section id="sample" className="border-t border-slate-800/60 py-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-8">
          <SectionHeader eyebrow="Sample Report" title="See exactly what you'll receive" desc="A preview of the intelligence Glexis delivers. Every analysis is rendered with this level of clarity." />
          <motion.div {...fade} className="mt-14 mx-auto max-w-3xl rounded-2xl border border-slate-800 bg-[#0A1124] p-6 sm:p-8">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-800 pb-4">
              <span className="font-mono text-xs text-slate-500">glexis · analysis-report-#A2F19</span>
              <div className="flex items-center gap-3">
                <span className="rounded-full border border-amber-400/40 bg-amber-400/10 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-amber-300">Illustrative Sample</span>
                <span className="flex items-center gap-2 text-xs text-slate-500"><span className="h-2 w-2 rounded-full bg-emerald-400" /> completed</span>
              </div>
            </div>

            {/* Panel 1 — Summary dashboard */}
            <div className="mt-6 flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">Overall Confidence</p>
                <div className="flex items-baseline gap-2">
                  <span className="font-mono text-5xl font-bold text-cyan-400 gx-text-glow">{SAMPLE.overall.score}</span>
                  <span className="text-sm text-slate-400">{SAMPLE.overall.label}</span>
                </div>
              </div>
            </div>
            <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
              {SAMPLE.metrics.map(([l, v]) => (
                <div key={l} className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
                  <p className="text-xs text-slate-500">{l}</p>
                  <p className="mt-1 font-mono text-lg font-semibold text-white">{v}</p>
                </div>
              ))}
            </div>
            <div className="mt-6 rounded-lg border border-slate-800 bg-slate-900/40 p-5">
              <p className="text-xs uppercase tracking-wide text-slate-500 mb-2">AI Summary</p>
              <p className="text-sm text-slate-300 leading-relaxed">{SAMPLE.summary}</p>
            </div>

            {/* Additional report panels */}
            <div className="mt-8 space-y-5 border-t border-slate-800 pt-8">
              <ReportPanel label="Conversation Overview">
                <dl className="grid gap-3 sm:grid-cols-2">
                  {SAMPLE.overview.map(([k, v]) => (
                    <div key={k}>
                      <dt className="text-xs text-slate-500">{k}</dt>
                      <dd className="mt-0.5 text-sm text-slate-300">{v}</dd>
                    </div>
                  ))}
                </dl>
              </ReportPanel>

              <ReportPanel label="Transcript Excerpts">
                <ul className="space-y-2">
                  {SAMPLE.transcript.map(([t, s, l], i) => (
                    <li key={i} className="flex gap-3 text-sm">
                      <span className="shrink-0 font-mono text-xs text-cyan-400/80 pt-0.5">{t}</span>
                      <span className="text-slate-300"><span className="font-semibold text-slate-200">{s}:</span> {l}</span>
                    </li>
                  ))}
                </ul>
              </ReportPanel>

              <ReportPanel label="Communication Indicators">
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  {SAMPLE.indicators.map(([k, v]) => (
                    <div key={k} className="rounded-lg border border-slate-800 bg-[#0A1124] p-3">
                      <p className="text-xs text-slate-500">{k}</p>
                      <p className="mt-1 font-mono text-sm font-semibold text-white">{v}</p>
                    </div>
                  ))}
                </div>
              </ReportPanel>

              <ReportPanel label="Emotional and Behavioral Patterns">
                <BulletList items={SAMPLE.patterns} />
              </ReportPanel>

              <ReportPanel label="Inconsistencies or Areas Requiring Context">
                <BulletList items={SAMPLE.contextAreas} />
              </ReportPanel>

              <ReportPanel label="Communication Strengths">
                <BulletList items={SAMPLE.strengths} />
              </ReportPanel>

              <ReportPanel label="Practical Takeaways">
                <BulletList items={SAMPLE.takeaways} />
              </ReportPanel>

              <ReportPanel label="Suggested Questions for a Future Conversation">
                <BulletList items={SAMPLE.questions} />
              </ReportPanel>
            </div>

            <div className="mt-8 rounded-lg border border-slate-800 bg-slate-900/40 p-4">
              <p className="text-xs leading-relaxed text-slate-500">{SAMPLE_DISCLAIMER}</p>
            </div>

            <div className="mt-6 text-center">
              <Button
                onClick={downloadSamplePdf}
                data-testid="sample-download-pdf"
                className="h-12 px-8 bg-cyan-400 text-black font-semibold hover:bg-cyan-300 gx-glow"
              >
                <Download className="h-4 w-4 mr-2" /> Download Sample PDF
              </Button>
              <p className="mt-3 text-xs text-slate-600">Full illustrative report — same layout your real analysis uses.</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="border-t border-slate-800/60 py-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-8">
          <SectionHeader eyebrow="Pricing" title="Simple, transparent access" desc="Start with a single analysis, or unlock continuous intelligence." />
          <p className="mt-4 text-center text-sm text-cyan-300">New users receive 1 complimentary full analysis — use within 24 hours</p>
          <div className="mt-14 grid gap-6 md:grid-cols-2 max-w-3xl mx-auto">
            <div className="rounded-xl border border-slate-800 bg-[#0A1124] p-8">
              <h3 className="font-display text-xl font-bold text-white">Single Analysis</h3>
              <div className="mt-2 flex items-baseline gap-1">
                <span className="font-mono text-4xl font-bold text-white">$1</span>
                <span className="text-slate-500">one-time</span>
              </div>
              <p className="mt-2 text-sm text-slate-400">One complete communication analysis. No subscription required.</p>
              <ul className="mt-6 space-y-3 text-sm text-slate-300">
                {["One complete communication analysis", "Full transcript included", "Communication confidence scoring"].map((f) => (
                  <li key={f} className="flex items-center gap-2"><Check className="h-4 w-4 text-cyan-400" /> {f}</li>
                ))}
              </ul>
              <Button asChild className="mt-8 w-full bg-slate-800 text-white hover:bg-slate-700 border border-slate-700">
                <Link to={primaryTo} data-testid="pricing-single-cta">Start for $1</Link>
              </Button>
            </div>
            <div className="relative rounded-xl border border-cyan-400/40 bg-[#0A1124] p-8 gx-glow">
              <span className="absolute -top-3 left-8 rounded-full bg-cyan-400 px-3 py-1 text-xs font-semibold text-black">Most Popular</span>
              <h3 className="font-display text-xl font-bold text-white">Monthly Subscription</h3>
              <div className="mt-2 flex items-baseline gap-1">
                <span className="font-mono text-4xl font-bold text-white">$36.95</span>
                <span className="text-slate-500">per month</span>
              </div>
              <p className="mt-2 text-sm text-slate-400">Multiple uploads and long-term communication trend analysis.</p>
              <ul className="mt-6 space-y-3 text-sm text-slate-300">
                {["Multiple uploads every month", "Long-term trend analysis", "Cross-conversation tracking", "Downloadable reports"].map((f) => (
                  <li key={f} className="flex items-center gap-2"><Check className="h-4 w-4 text-cyan-400" /> {f}</li>
                ))}
              </ul>
              <Button asChild className="mt-8 w-full bg-cyan-400 text-black font-semibold hover:bg-cyan-300">
                <Link to={primaryTo} data-testid="pricing-sub-cta">Subscribe monthly</Link>
              </Button>
            </div>
          </div>
          <p className="mt-6 text-center text-xs text-slate-600">Secure checkout inside the Glexis app. Cancel anytime.</p>
        </div>
      </section>

      {/* Referral */}
      <section className="border-t border-slate-800/60 py-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-8">
          <SectionHeader eyebrow="Referral Program" title="Share Glexis, earn free analyses" desc="Invite friends and earn 1 free analysis for every successful referral." />
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {[
              { icon: Users, t: "Invite Friends", d: "Share your personal referral link with friends who need clarity." },
              { icon: ArrowRight, t: "They Join Glexis", d: "Your friend signs up and runs their first analysis." },
              { icon: Check, t: "Earn 1 Free Analysis", d: "You receive 1 free analysis for every successful referral." },
            ].map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div key={s.t} {...fade} transition={{ duration: 0.5, delay: i * 0.08 }}
                  className="rounded-xl border border-slate-800 bg-[#0A1124] p-8">
                  <div className="flex h-11 w-11 items-center justify-center rounded-lg border border-cyan-400/40 bg-cyan-400/10">
                    <Icon className="h-5 w-5 text-cyan-400" />
                  </div>
                  <h3 className="mt-5 font-display text-lg font-semibold text-white">{s.t}</h3>
                  <p className="mt-2 text-sm text-slate-400">{s.d}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Security */}
      <section className="border-t border-slate-800/60 py-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-8">
          <SectionHeader eyebrow="Security & Privacy" title="Your files, handled securely" desc="Privacy-first engineering at every step of the analysis pipeline." />
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {[
              { icon: ShieldCheck, t: "Secure Processing", d: "Every file is transmitted over encrypted HTTPS connections and handled securely." },
              { icon: LockKeyhole, t: "Privacy Focused", d: "Your conversations are yours. We never sell your personal data." },
              { icon: FileAudio, t: "Files Handled Securely", d: "Uploads are handled securely with privacy-conscious processing practices." },
            ].map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div key={s.t} {...fade} transition={{ duration: 0.5, delay: i * 0.08 }}
                  className="rounded-xl border border-slate-800 bg-[#0A1124] p-8">
                  <div className="flex h-11 w-11 items-center justify-center rounded-lg border border-cyan-400/40 bg-cyan-400/10">
                    <Icon className="h-5 w-5 text-cyan-400" />
                  </div>
                  <h3 className="mt-5 font-display text-lg font-semibold text-white">{s.t}</h3>
                  <p className="mt-2 text-sm text-slate-400">{s.d}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="border-t border-slate-800/60 py-24">
        <div className="mx-auto max-w-3xl px-4 sm:px-8">
          <SectionHeader eyebrow="FAQ" title="Questions, answered" />
          <Accordion type="single" collapsible className="mt-12">
            {[
              ["What does Glexis analyze?", "Glexis analyzes communication patterns in your uploaded audio — clarity, points of ambiguity, information that appears to be missing, and useful follow-up questions. It supports clearer decision-making and does not make determinations about honesty, guilt, or intent."],
              ["Is Glexis a lie detector?", "No. Glexis is a communication-clarity and decision-support tool. It does not detect deception and makes no legal or medical conclusions."],
              ["Are my files secure?", "Files are transmitted over encrypted HTTPS connections and handled with privacy-conscious practices."],
              ["How do free analyses work?", "New users receive one complimentary full analysis that must be used within 24 hours of signing up."],
              ["Which devices does Glexis work on?", "Glexis runs in your browser on iPhone, Android, Windows, and Mac — no app store required."],
            ].map(([q, a], i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-slate-800" data-testid={`faq-${i}`}>
                <AccordionTrigger className="text-left text-white hover:text-cyan-300 hover:no-underline">{q}</AccordionTrigger>
                <AccordionContent className="text-slate-400">{a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="border-t border-slate-800/60 py-24">
        <div className="mx-auto max-w-4xl px-4 sm:px-8 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-white">Ready for real clarity?</h2>
          <p className="mt-4 text-slate-400">Start with your complimentary analysis today.</p>
          <Button asChild size="lg" className="mt-8 h-12 px-8 bg-cyan-400 text-black font-semibold hover:bg-cyan-300 gx-glow">
            <Link to={primaryTo} data-testid="footer-cta">Get started free <ArrowRight className="h-4 w-4 ml-2" /></Link>
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-800/60 py-10">
        <div className="mx-auto max-w-6xl px-4 sm:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <Logo variant="icon" size="md" className="rounded-lg" />
          <p className="text-xs text-slate-600 max-w-md text-center sm:text-right">
            Glexis provides communication-clarity insights for decision support only. It is not a lie
            detector and makes no claims about guilt, deception, intent, or legal/medical matters.
          </p>
        </div>
      </footer>
    </div>
  );
}

function SectionHeader({ eyebrow, title, desc }) {
  return (
    <motion.div {...fade} className="mx-auto max-w-2xl text-center">
      <span className="text-xs font-semibold uppercase tracking-[0.2em] text-cyan-400">{eyebrow}</span>
      <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight text-white">{title}</h2>
      {desc && <p className="mt-4 text-slate-400">{desc}</p>}
    </motion.div>
  );
}

function ReportPanel({ label, children }) {
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-5">
      <p className="text-xs uppercase tracking-wide text-slate-500 mb-3">{label}</p>
      {children}
    </div>
  );
}

function BulletList({ items }) {
  return (
    <ul className="space-y-2">
      {items.map((x, i) => (
        <li key={i} className="flex items-start gap-2.5 text-sm text-slate-300">
          <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-cyan-400" aria-hidden="true" />
          <span className="leading-relaxed">{x}</span>
        </li>
      ))}
    </ul>
  );
}
